import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Grid } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

export interface RoutalAccordionProps {
  id?: string;
  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  expanded?: boolean;
  headerStyles?: React.CSSProperties;
  headerContent?: string | React.ReactElement;
  expandIconStyles?: React.CSSProperties;
  expandIcon?: React.ReactElement;
  collapseIcon?: React.ReactElement;
  onToggle?: (expanded: boolean) => void;
}

const generateRandomId = () => `routal-accordion-${Math.random()}`;

/**
 * General purpose accordion component.
 * @param props
 */
export const RoutalAccordion = ({
  id,
  style,
  contentStyle,
  expandIconStyles,
  headerStyles,
  headerContent,
  children,
  expanded,
  expandIcon,
  collapseIcon,
  onToggle,
}: React.PropsWithChildren<RoutalAccordionProps>) => {
  const [open, setOpen] = useState<boolean>(expanded ?? false);

  const keyRef = useRef<string>(id ?? generateRandomId());

  useEffect(() => {
    if (keyRef.current === null && keyRef.current !== id) {
      keyRef.current = id ?? generateRandomId();
    }
  }, [id]);

  useEffect(() => {
    if (expanded && expanded !== open) {
      toggleCollapse(expanded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const toggleCollapse = (expanded: boolean) => {
    setOpen(expanded);
    if (onToggle) onToggle(expanded);
  };

  const getExpandIcons = () => {
    if (open) {
      if (collapseIcon) {
        return React.cloneElement(collapseIcon, {
          onClick: () => toggleCollapse(!open),
        });
      }
      return <ExpandLessIcon width={12} height={12} onClick={() => toggleCollapse(!open)} />;
    }
    if (expandIcon) {
      return React.cloneElement(expandIcon, {
        onClick: () => toggleCollapse(!open),
      });
    }
    return <ExpandMoreIcon width={12} height={12} onClick={() => toggleCollapse(!open)} />;
  };

  return (
    <Grid
      id={keyRef.current}
      container
      direction="column"
      style={{
        ...(open ? { flex: 1 } : {}),
        ...style,
      }}
    >
      <Grid
        container
        direction="row"
        wrap="nowrap"
        onClick={() => toggleCollapse(!open)}
        style={{
          cursor: `pointer`,
          padding: `8px 4px`,
          ...(headerStyles ?? {}),
        }}
      >
        <Grid item style={expandIconStyles}>
          {getExpandIcons()}
        </Grid>
        {headerContent}
      </Grid>

      {open ? (
        <Grid container direction="column" style={contentStyle ?? {}}>
          {children}
        </Grid>
      ) : null}
    </Grid>
  );
};
